import React, { useState } from 'react';
import { HelpCircle, ChevronDown } from 'lucide-react';
import type { UrlSource } from '../api';

/**
 * Was die Quellen bedeuten — und vor allem, was sie *nicht* sagen.
 *
 * WARUM ES DAS BRAUCHT: Die Zahlen nebeneinander laden zum falschen Schluss
 * ein. „Web-Archiv 4 200, SERP 180" liest sich wie „SERP hat kaum etwas
 * gefunden" — richtig ist: die eine Quelle sammelt seit zwanzig Jahren, die
 * andere ist bei einigen hundert gedeckelt und war nie als Bestandsliste
 * gedacht. Die Zahlen sind **nicht vergleichbar**, und ohne diesen Satz
 * daneben vergleicht sie jeder.
 *
 * Die Texte stammen aus den Kommentaren der Quellen selbst
 * (`backend/src/services/url-quellen/`). Dort stand das Wissen bisher
 * ausschließlich — richtig aufgeschrieben, nur an einer Stelle, die niemand
 * liest, der auf die Zahlen sieht.
 */

interface Erklaerung {
  /** Ein Satz. Landet als Tooltip an der Marke. */
  kurz: string;
  /** Was die Quelle beantwortet. */
  sagt: string;
  /** Was sie prinzipiell nicht kann. Der wichtigere Teil. */
  sagtNicht: string;
  /** Wie eine hohe oder niedrige Zahl zu lesen ist. */
  zahl: string;
}

export const QUELLEN_HILFE: Record<UrlSource, Erklaerung> = {
  SITEMAP: {
    kurz: 'Was die Seite selbst über sich behauptet.',
    sagt: 'Was der Betreiber indexiert haben *will*. Die einzige Quelle, hinter der eine Absicht steht.',
    sagtNicht:
      'Ob es die Seiten wirklich gibt und ob sie jemand findet. Eine Sitemap ist eine Behauptung, keine Prüfung.',
    zahl: 'Null heißt meistens „keine Sitemap gefunden" — nicht „die Seite ist leer".',
  },
  CRAWL: {
    kurz: 'Was von der Startseite aus verlinkt erreichbar ist — Stand heute.',
    sagt: 'Den Ist-Zustand: was ein Besucher durch Klicken erreichen könnte.',
    sagtNicht:
      'Was nicht verlinkt ist. Eine Seite ohne eingehenden Link bleibt unsichtbar, auch wenn sie online steht und indexiert ist.',
    zahl: 'Die einzige Zahl hier, die sich auf *jetzt* bezieht. Alle anderen mischen Zeiten.',
  },
  WAYBACK: {
    kurz: 'Was es unter dieser Domain je gab. Ein Gedächtnis, kein Spiegel.',
    sagt: 'Alles, was das Internet Archive je gesehen hat — auch aus 2014.',
    sagtNicht:
      'Ob eine URL heute noch existiert. Eine Adresse, die seit 2016 ins Leere läuft, steht hier gleichberechtigt neben der Startseite.',
    zahl:
      'Regelmäßig die höchste Zahl, und das ist kein Widerspruch. Für die Frage „was hängt noch an einer Domain, die wir abstoßen wollen" die nützlichste Quelle.',
  },
  COMMONCRAWL: {
    kurz: 'Ein großer freier Crawl des Webs — mit Teilabdeckung.',
    sagt: 'Was in den jüngsten öffentlichen Crawl-Sammlungen steht.',
    sagtNicht:
      'Alles. Common Crawl besucht nicht jede Seite und nicht regelmäßig. Eine niedrige Zahl heißt nicht, dass die Domain klein ist.',
    zahl: 'Meist deutlich unter dem Web-Archiv, und meist ohne Aussagekraft über die Größe der Seite.',
  },
  SERP: {
    kurz: 'Wie die Domain in einer Suche aussieht — prinzipiell unvollständig.',
    sagt:
      'Was eine `site:`-Suche zeigen würde. Also: wie die Domain jemandem begegnet, der danach sucht.',
    sagtNicht:
      'Wie viel wirklich indexiert ist. Google deckelt `site:` bei einigen hundert Treffern und nennt seine Trefferzahl selbst eine Schätzung. Ein bezahlter Dienst umgeht das nicht — er blättert nur durch dieselben gedeckelten Seiten. Eine Domain mit fünftausend Seiten liefert hier dreihundert. Wer den echten Indexstand braucht, braucht die Search Console.',
    zahl:
      'Fast immer die kleinste Zahl, und das ist erwartet — kein Zeichen dafür, dass die Suche wenig gefunden hätte. Achtung: **jeder Abruf kostet Geld**, deshalb wird nicht wiederholt und hart gedeckelt.',
  },
  GSC: {
    kurz: 'Google Search Console — welche Seiten Suchtreffer bekommen haben. Setzt eine verifizierte Property voraus.',
    sagt:
      'Welche URLs in den letzten 16 Monaten in Suchergebnissen erschienen sind. Deutlich mehr als der SERP-Dienst und ohne dessen Deckel — bis 25 000 Zeilen je Abruf.',
    sagtNicht:
      'Was indexiert ist. Das ist der häufigste Irrtum zu dieser Quelle, und er wäre hier fast auch mir passiert: die Schnittstelle hat **keinen Aufruf, der indexierte Seiten auflistet**. Was sie liefert, sind Seiten mit Sichtbarkeit. Eine indexierte Seite, die nie jemand angezeigt bekam, fehlt. Den Indexstand gibt es nur je einzelner URL (2 000 Abfragen am Tag).',
    zahl:
      'Beantwortet „was von dieser Domain ist bei Google sichtbar" — eine andere und meist nützlichere Frage als „was ist indexiert".',
  },
  BING: {
    kurz: 'Bing Webmaster Tools. Setzt eine dort verifizierte Domain voraus.',
    sagt: 'Dasselbe wie die Search Console, nur für Bing: was dort Sichtbarkeit hatte.',
    sagtNicht:
      'Was Google kennt — die Indizes unterscheiden sich erheblich. Und ebenso wenig wie bei Google eine vollständige Liste des Indexbestands.',
    zahl: 'Zum Abgleich nützlich, als alleinige Grundlage zu schmal.',
  },
  MANUAL: {
    kurz: 'Von Hand eingetragen.',
    sagt: 'Was jemand hier bewusst hinterlegt hat.',
    sagtNicht: 'Nichts darüber hinaus — es wurde nicht geprüft.',
    zahl: 'Zählt mit, ist aber kein Fund, sondern eine Eingabe.',
  },
};

/**
 * Der Teil, der aus Zahlen eine Auskunft macht.
 *
 * Die Einzelbeschreibungen oben erklären je eine Quelle. Interessant sind aber
 * die **Differenzen** — und die sieht man nur, wenn jemand sagt, worauf zu
 * achten ist.
 */
const DIFFERENZEN: Array<{ muster: string; bedeutet: string }> = [
  {
    muster: 'In der Sitemap, aber nicht im SERP',
    bedeutet:
      'Der Betreiber will es indexiert haben, in der Suche taucht es nicht auf. Entweder noch nicht erfasst, ausgeschlossen (noindex, robots.txt) — oder es fiel unter den Deckel der SERP-Abfrage.',
  },
  {
    muster: 'Im Web-Archiv, aber nicht im eigenen Crawl',
    bedeutet:
      'Gab es einmal, ist heute nicht mehr verlinkt. Der Regelfall bei alten Domains — und genau die Liste, die man vor dem Abstoßen durchsieht.',
  },
  {
    muster: 'Im eigenen Crawl, aber in keiner Suchquelle',
    bedeutet:
      'Erreichbar, aber offenbar nirgends indexiert. Bei einer Seite, die gefunden werden soll, ein Befund; bei einem internen Bereich das erwünschte Verhalten.',
  },
  {
    muster: 'Alle Quellen niedrig, Sitemap null',
    bedeutet:
      'Meist eine Domain, die nur weitergeleitet oder geparkt ist. Vor dem Abstoßen trotzdem das Web-Archiv ansehen — dort steht, was es früher war.',
  },
];

/**
 * Quellen, die eine hinterlegte Domain voraussetzen.
 *
 * Bing und die Search Console liefern nur zu Domains, die im jeweiligen
 * Werkzeug **eingetragen und verifiziert** sind. Fehlt das, kommt kein Fehler,
 * sondern eine leere Antwort — und leer liest sich wie „kennt die Domain
 * nicht", was etwas ganz anderes ist als „wir dürfen nicht fragen". Deshalb
 * steht es hier dran und nicht nur im Kleingedruckten.
 */
const BRAUCHT_VERIFIZIERUNG: UrlSource[] = ['GSC', 'BING'];

export default function QuellenHilfe({ nurQuellen }: { nurQuellen?: UrlSource[] }) {
  const [offen, setOffen] = useState(false);
  const quellen = (nurQuellen ?? (Object.keys(QUELLEN_HILFE) as UrlSource[])).filter(
    (q) => q !== 'MANUAL',
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-surface">
      <button
        onClick={() => setOffen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
      >
        <HelpCircle size={14} className="text-slate-400" />
        <span className="font-medium">Was sagen mir diese Zahlen?</span>
        <ChevronDown
          size={14}
          className={`ml-auto text-slate-400 transition-transform ${offen ? 'rotate-180' : ''}`}
        />
      </button>

      {offen && (
        <div className="space-y-4 border-t border-slate-100 px-3 py-3 text-xs">
          {/* Der wichtigste Satz zuerst — er verhindert den häufigsten Fehlschluss. */}
          <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-amber-900">
            <strong>Die Zahlen sind nicht vergleichbar.</strong> Jede Quelle beantwortet eine
            andere Frage, über einen anderen Zeitraum, mit einer anderen Vollständigkeit. Dass
            das Web-Archiv zwanzigmal so viel findet wie der SERP-Dienst, ist der Normalfall
            und kein Hinweis auf ein Problem.
          </p>

          <div className="space-y-3">
            {quellen.map((q) => {
              const e = QUELLEN_HILFE[q];
              return (
                <div key={q} className="border-l-2 border-slate-200 pl-2.5">
                  <p className="font-medium text-slate-700">
                    {QUELLEN_NAME[q]}
                    {BRAUCHT_VERIFIZIERUNG.includes(q) && (
                      <span className="ml-1.5 rounded bg-amber-50 px-1 py-0.5 text-[10px] font-normal text-amber-700">
                        Domain muss dort verifiziert sein
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-slate-600">{e.sagt}</p>
                  <p className="mt-0.5 text-slate-500">
                    <span className="font-medium text-slate-600">Sagt nicht: </span>
                    {e.sagtNicht}
                  </p>
                  <p className="mt-0.5 text-slate-400">{e.zahl}</p>
                </div>
              );
            })}
          </div>

          <div>
            <p className="mb-1.5 font-medium text-slate-700">
              Interessant sind die Unterschiede, nicht die Summen
            </p>
            <ul className="space-y-1.5">
              {DIFFERENZEN.map((d) => (
                <li key={d.muster} className="border-l-2 border-brand-200 pl-2.5">
                  <span className="font-medium text-slate-700">{d.muster}</span>
                  <span className="text-slate-500"> — {d.bedeutet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-slate-50 px-2.5 py-2">
            <p className="mb-1 font-medium text-slate-700">
              Bing und Search Console brauchen eine hinterlegte Domain
            </p>
            <p className="text-slate-600">
              Beide liefern nur zu Domains, die im jeweiligen Werkzeug eingetragen und
              verifiziert sind. Bei Google kommt eine zweite Bedingung dazu: das Dienstkonto
              muss dort <em>zusätzlich</em> als Nutzer der Property eingetragen sein — ein
              gültiger Schlüssel allein sieht sonst gar keine Property. Fehlt eines von beidem,
              sagt der Scan es im Klartext, statt eine leere Liste zu zeigen.
            </p>
          </div>

          <p className="text-slate-400">
            „Gedeckelt" an einer Zeile heißt: die Quelle hat abgeschnitten, es gibt mehr. Die
            Zahl ist dann eine Untergrenze, kein Ergebnis.
          </p>
        </div>
      )}
    </div>
  );
}

/** Muss zu QUELLEN_TEXT in DomainUrlsPage passen. */
const QUELLEN_NAME: Record<UrlSource, string> = {
  SITEMAP: 'Sitemap',
  CRAWL: 'Eigener Crawl',
  WAYBACK: 'Web-Archiv',
  COMMONCRAWL: 'Common Crawl',
  GSC: 'Search Console',
  BING: 'Bing',
  SERP: 'SERP-Dienst',
  MANUAL: 'Von Hand',
};
